import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import "./styles/admin-dark.css";
import ScrollToTop from "./components/ScrollToTop";
import BackToTopFAB from "./components/BackToTopFAB";
import GAListener from "./lib/ga";

// -------- Public pages (eager) --------
// These power the marketing site + signup funnel. They MUST be in the main
// bundle so the landing page is instant.
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Address from "./pages/Address";
import PlanPage from "./pages/PlanPage";
import Enroll from "./pages/Enroll";
import EnrollmentComplete from "./pages/EnrollmentComplete";
import ChatPaymentResult from "./pages/ChatPaymentResult";
import FAQ from "./pages/FAQ";
import LegalPage from "./pages/LegalPage";
import ErrorPage from "./pages/ErrorPage";
import About from "./pages/About";
import HowItWorks from "./pages/HowItWorks";
import Support from "./pages/Support";
import Contact from "./pages/Contact";
import Plans from "./pages/Plans";
import EFL from "./pages/EFL";
import ReferralProgram from "./pages/ReferralProgram";
import Account from "./pages/Account";
import PaySuccess from "./pages/PaySuccess";
import PayCancelled from "./pages/PayCancelled";
import UploadDocs from "./pages/UploadDocs";
import CityPage from "./pages/CityPage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ChatWidget from "./components/ChatWidget";

// -------- Admin pages (lazy) --------
// Code-split so visitors of the public site don't download the CRM (Stripe
// dashboard, Recharts, Customer Ficha drawer, etc).
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminForgotPassword = lazy(() => import("./pages/AdminForgotPassword"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));
const AdminFraudAudit = lazy(() => import("./pages/admin/AdminFraudAudit"));
const AdminFraudReview = lazy(() => import("./pages/admin/AdminFraudReview"));
const AdminDepositsROI = lazy(() => import("./pages/admin/AdminDepositsROI"));
const AdminDepositRefunds = lazy(() => import("./pages/admin/AdminDepositRefunds"));
const AdminChatInbox = lazy(() => import("./pages/admin/AdminChatInbox"));
const AdminChatAnalytics = lazy(() => import("./pages/admin/AdminChatAnalytics"));
const AdminLeadsLost = lazy(() => import("./pages/admin/AdminLeadsLost"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAgents = lazy(() => import("./pages/admin/AdminAgents"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));
const AdminSystemEmails = lazy(() => import("./pages/admin/AdminSystemEmails"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminTags = lazy(() => import("./pages/admin/AdminTags"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminStripeEvents = lazy(() => import("./pages/admin/AdminStripeEvents"));
const AdminDeposits = lazy(() => import("./pages/admin/AdminDeposits"));
const AdminPaymentsFailed = lazy(() => import("./pages/admin/AdminPaymentsFailed"));
const AdminProductionHealth = lazy(() => import("./pages/admin/AdminProductionHealth"));
const AdminReconnections = lazy(() => import("./pages/admin/AdminReconnections"));
const DepositSuccess = lazy(() => import("./pages/DepositSuccess"));
const DepositCancel = lazy(() => import("./pages/DepositCancel"));
const AdminProfile = lazy(() => import("./pages/admin/AdminProfile"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminInbox = lazy(() => import("./pages/admin/AdminInbox"));
const AdminSecurity = lazy(() => import("./pages/admin/AdminSecurity"));
const AdminCustomersDisconnected = lazy(() => import("./pages/admin/AdminCustomersDisconnected"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const ServicePlacement = lazy(() => import("./pages/admin/ServicePlacement"));
const AdminCommissions = lazy(() => import("./pages/admin/AdminCommissions"));
const AdminPortalAccess = lazy(() => import("./pages/admin/AdminPortalAccess"));
const AdminPortalView = lazy(() => import("./pages/admin/AdminPortalView"));

// -------- Customer portal (lazy) --------
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalSignup = lazy(() => import("./pages/portal/PortalSignup"));
const PortalMagicLinkAuth = lazy(() => import("./pages/portal/PortalMagicLinkAuth"));
const PortalForgotPassword = lazy(() => import("./pages/portal/PortalForgotPassword"));
const PortalQuickPay = lazy(() => import("./pages/portal/PortalQuickPay"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalBills = lazy(() => import("./pages/portal/PortalBills"));
const PortalRefer = lazy(() => import("./pages/portal/PortalRefer"));
const PortalProfile = lazy(() => import("./pages/portal/PortalProfile"));
const PortalPlan = lazy(() => import("./pages/portal/PortalPlan"));
const PortalMessages = lazy(() => import("./pages/portal/PortalMessages"));
const PortalSettings = lazy(() => import("./pages/portal/PortalSettings"));
const PortalPayments = lazy(() => import("./pages/portal/PortalPayments"));
const PortalDocuments = lazy(() => import("./pages/portal/PortalDocuments"));

// Promoter portal (Feb 2026)
const PromoterJoin = lazy(() => import("./pages/promoter/PromoterJoin"));
const PromoterLogin = lazy(() => import("./pages/promoter/PromoterLogin"));
const PromoterDashboard = lazy(() => import("./pages/promoter/PromoterDashboard"));

// Admin promoter management
const AdminPromoters = lazy(() => import("./pages/admin/AdminPromoters"));
const AdminPromoterDetail = lazy(() => import("./pages/admin/AdminPromoterDetail"));

function ReferralCapture() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      localStorage.setItem("velcor_ref", ref.toUpperCase().trim());
    }
  }, []);
  return null;
}

// Lightweight loader shown while a lazy chunk downloads. Intentionally
// minimal (no spinners) — the perceived wait is shorter than any animation.
function ChunkFallback() {
  return (
    <div
      className="min-h-screen w-full grid place-items-center bg-slate-50 dark:bg-slate-950"
      data-testid="chunk-fallback"
    >
      <div className="w-8 h-8 rounded-full border-2 border-velcor-green border-r-transparent animate-spin" />
    </div>
  );
}

// Show the floating chat widget only on public marketing pages — not in
// /admin (staff have the admin inbox) or /portal (logged-in customers have
// dedicated support flows).
function PublicChatWidget() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isPortal = pathname.startsWith("/portal");
  if (isAdmin || isPortal) return null;
  return <ChatWidget />;
}

export default function App() {
  return (
    <>
      <ReferralCapture />
      <ScrollToTop />
      <GAListener />
      <PublicChatWidget />
      <Suspense fallback={<ChunkFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/address" element={<Address />} />
          <Route path="/apartments" element={<PlanPage serviceType="apartments" />} />
          <Route path="/residential" element={<PlanPage serviceType="residential" />} />
          <Route path="/commercial" element={<PlanPage serviceType="commercial" />} />
          <Route path="/enroll" element={<Enroll />} />
          <Route path="/enrollment-complete" element={<EnrollmentComplete />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/referidos" element={<ReferralProgram />} />
          <Route path="/referrals" element={<ReferralProgram />} />
          <Route path="/electricity-facts-label/:planId" element={<EFL />} />
          <Route path="/electricity-facts-label" element={<EFL />} />
          <Route path="/account" element={<Account />} />
          <Route path="/privacy" element={<LegalPage namespace="privacyPage" />} />
          <Route path="/terms" element={<LegalPage namespace="termsPage" />} />
          <Route path="/cookies" element={<LegalPage namespace="cookiesPage" />} />
          <Route path="/your-rights" element={<LegalPage namespace="yracPage" />} />
          <Route path="/pago/exito" element={<PaySuccess />} />
          {/* /payment-complete — canonical Google Ads conversion URL for
              completed payments. Same visual page as /pago/exito.
              /pago/exito kept as an alias for any Stripe Checkout sessions
              that may still have it cached as success_url at the moment of
              the rollout. */}
          <Route path="/payment-complete" element={<PaySuccess />} />
          <Route path="/pago/cancelado" element={<PayCancelled />} />
          {/* Chat-driven enrollment payment landing pages */}
          <Route path="/chat/payment-success" element={<ChatPaymentResult result="success" />} />
          <Route path="/chat/payment-cancel" element={<ChatPaymentResult result="cancel" />} />
          {/* Public magic-link page where chat-wizard customers upload
              ID + proof-of-address when fraud_score is elevated. */}
          <Route path="/upload-docs/:leadId" element={<UploadDocs />} />

          {/* SEO city landing pages — explicit routes (React Router v6 does
              not support `prefix-:param` patterns, so we register each city). */}
          <Route path="/electricidad-houston" element={<CityPage lang="es" slug="houston" />} />
          <Route path="/electricity-houston" element={<CityPage lang="en" slug="houston" />} />
          <Route path="/electricidad-dallas" element={<CityPage lang="es" slug="dallas" />} />
          <Route path="/electricity-dallas" element={<CityPage lang="en" slug="dallas" />} />
          <Route path="/electricidad-san-antonio" element={<CityPage lang="es" slug="san-antonio" />} />
          <Route path="/electricity-san-antonio" element={<CityPage lang="en" slug="san-antonio" />} />
          <Route path="/electricidad-fort-worth" element={<CityPage lang="es" slug="fort-worth" />} />
          <Route path="/electricity-fort-worth" element={<CityPage lang="en" slug="fort-worth" />} />
          <Route path="/electricidad-arlington" element={<CityPage lang="es" slug="arlington" />} />
          <Route path="/electricity-arlington" element={<CityPage lang="en" slug="arlington" />} />
          <Route path="/electricidad-corpus-christi" element={<CityPage lang="es" slug="corpus-christi" />} />
          <Route path="/electricity-corpus-christi" element={<CityPage lang="en" slug="corpus-christi" />} />
          <Route path="/electricidad-galveston" element={<CityPage lang="es" slug="galveston" />} />
          <Route path="/electricity-galveston" element={<CityPage lang="en" slug="galveston" />} />
          <Route path="/electricidad-waco" element={<CityPage lang="es" slug="waco" />} />
          <Route path="/electricity-waco" element={<CityPage lang="en" slug="waco" />} />

          {/* Short, friendly city aliases — same content as the SEO routes
              above but with a clean, memorable URL the customer can share
              by WhatsApp/SMS (e.g. velcorenergy.com/houston).
              `noLangRedirect` keeps the short URL intact regardless of
              browser language preference. Content adapts to user lang
              via i18n. */}
          <Route path="/houston" element={<CityPage lang="es" slug="houston" noLangRedirect />} />
          <Route path="/dallas" element={<CityPage lang="es" slug="dallas" noLangRedirect />} />
          <Route path="/san-antonio" element={<CityPage lang="es" slug="san-antonio" noLangRedirect />} />
          <Route path="/fort-worth" element={<CityPage lang="es" slug="fort-worth" noLangRedirect />} />
          <Route path="/arlington" element={<CityPage lang="es" slug="arlington" noLangRedirect />} />
          <Route path="/corpus-christi" element={<CityPage lang="es" slug="corpus-christi" noLangRedirect />} />
          <Route path="/galveston" element={<CityPage lang="es" slug="galveston" noLangRedirect />} />
          <Route path="/waco" element={<CityPage lang="es" slug="waco" noLangRedirect />} />

          {/* Alias preferido por el usuario: /enroll-completed igual que /enrollment-complete */}
          <Route path="/enroll-completed" element={<EnrollmentComplete />} />

          {/* Blog */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          {/* Impersonation tab opens OUTSIDE the AdminLayout (no sidebar) so the admin
              sees the customer portal full-width with the amber banner on top. */}
          <Route path="/admin/portal-view/:email" element={<AdminPortalView />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="leads/lost" element={<AdminLeadsLost />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/disconnected" element={<AdminCustomersDisconnected />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="placement" element={<ServicePlacement />} />
            <Route path="portal-access" element={<AdminPortalAccess />} />
            <Route path="commissions" element={<AdminCommissions />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="system-emails" element={<AdminSystemEmails />} />
            <Route path="alerts" element={<AdminAlerts />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="tags" element={<AdminTags />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="funnel" element={<AdminFunnel />} />
            <Route path="fraud-audit" element={<AdminFraudAudit />} />
            <Route path="fraud-review" element={<AdminFraudReview />} />
            <Route path="deposits-roi" element={<AdminDepositsROI />} />
            <Route path="deposit-refunds" element={<AdminDepositRefunds />} />
            <Route path="chat-inbox" element={<AdminChatInbox />} />
            <Route path="chat-analytics" element={<AdminChatAnalytics />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="stripe" element={<AdminStripeEvents />} />
            <Route path="payments/failed" element={<AdminPaymentsFailed />} />
            <Route path="production-health" element={<AdminProductionHealth />} />
            <Route path="reconnections-pending" element={<AdminReconnections />} />
            <Route path="deposits" element={<AdminDeposits />} />
            <Route path="promoters" element={<AdminPromoters />} />
            <Route path="promoters/:code" element={<AdminPromoterDetail />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="security" element={<AdminSecurity />} />
          </Route>

          {/* Customer portal */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/quickpay" element={<PortalQuickPay />} />
          <Route path="/portal/deposit/success" element={<DepositSuccess />} />
          <Route path="/portal/deposit/cancel" element={<DepositCancel />} />
          <Route path="/portal/signup" element={<PortalSignup />} />
          <Route path="/portal/auth/link" element={<PortalMagicLinkAuth />} />
          <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalDashboard />} />
            <Route path="plan" element={<PortalPlan />} />
            <Route path="bills" element={<PortalBills />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="payments" element={<PortalPayments />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="refer" element={<PortalRefer />} />
            <Route path="settings" element={<PortalSettings />} />
            <Route path="profile" element={<PortalProfile />} />
          </Route>

          <Route path="/500" element={<ErrorPage variant="500" />} />

          {/* Promoter portal (public + authenticated) */}
          <Route path="/promoter/join/:token" element={<PromoterJoin />} />
          <Route path="/promoter/login" element={<PromoterLogin />} />
          <Route path="/promoter/dashboard" element={<PromoterDashboard />} />

          <Route path="*" element={<ErrorPage variant="404" />} />
        </Routes>
      </Suspense>
      <BackToTopFAB />
    </>
  );
}
